import type { CheckpointDef, StageDef } from './stages';
import type { CollisionWorld, MovingPlatform } from '../world/CollisionWorld';
import type { Pushable } from '../interact/Pushable';
import type { CharacterState } from '../character/CharacterStateMachine';

/** Pembaca kondisi pemain (disediakan MainGameScene). */
export interface RuntimePlayer {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  grounded: boolean;
  state: CharacterState;
}

/** Koneksi runtime -> scene (fungsi efek samping). */
export interface StageRuntimeCtx {
  world: CollisionWorld;
  player(): RuntimePlayer;
  pushables: Pushable[];
  setGateOpen(index: number): void;
  resetPlayer(x: number, z: number): void;
  respawnPushable(index: number, x: number, z: number): void;
  chase(): MovingPlatform | null;
  inFinishZone(x: number, z: number): boolean;
  crateSpeed(index: number): number;
  hint(text: string | null): void;
  objective(text: string | null): void;
}

const GROUNDED_STATES: ReadonlySet<CharacterState> = new Set(['Idle', 'Walk', 'Run']);

/**
 * Mesin progresi stage bergaya gate (anti-sequence-break):
 * - Checkpoint c1/c2/c3 divalidasi dari posisi + grounded state + kondisi
 *   crate (bukan sekadar posisi).
 * - Gate hanya terbuka saat checkpoint-nya terpenuhi (collider solid
 *   dikendalikan scene).
 * - Section 3: dinding pengejar (chase) dengan gagal -> reset.
 * - Watchdog crate: crate bukan-jembatan yang jatuh ke air/tersangkut
 *   di-respawn ke titik asal (anti-softlock).
 * - Finish: crate harus DIAM di pad selama `finishSettle.time` (bukan
 *   "kebetulan lewat").
 *
 * Hanya aktif untuk stage yang punya `gates`/`checkpoints` (ownsStage).
 */
export class StageRuntime {
  private def: StageDef;
  private cp: CheckpointDef | undefined;
  private flags = { c1: false, c2: false, c3: false };
  private finished = false;
  private sledTimer = 0;
  private waterGrace = new Map<number, number>();
  private retryCooldown = 0;
  /** Titik valid terakhir per crate (daratan kering): tujuan respawn
   *  watchdog — BUKAN spawn awal, supaya crate full-stage tak mental
   *  jauh ke belakang. */
  private lastValid: Array<{ x: number; z: number }> = [];

  constructor(
    def: StageDef,
    private ctx: StageRuntimeCtx,
  ) {
    this.def = def;
    this.cp = def.checkpoints;
    this.lastValid = ctx.pushables.map((c) => ({ x: c.pos.x, z: c.pos.z }));
  }

  get ownsStage(): boolean {
    return (this.def.gates?.length ?? 0) > 0 && !!this.cp;
  }

  get requiresSettle(): boolean {
    return this.def.finishSettle !== undefined;
  }

  /** True saat semua syarat finish stage1 terpenuhi (panggil onWin). */
  get win(): boolean {
    return this.finished;
  }

  update(delta: number): void {
    if (!this.ownsStage) return;
    if (this.finished) return;
    const p = this.ctx.player();
    this.retryCooldown = Math.max(0, this.retryCooldown - delta);

    this.evalC1(p);
    this.evalC2(p);
    this.evalC3(p, delta);
    this.watchdogCrates(delta);
    this.evalFinishSettle(delta);
    this.updateObjective();
    // Dinding chase padat HANYA saat menyapu; selebihnya hantu supaya
    // tak pernah jadi jalan buntu (istirahat, berhenti, selesai).
    const chase = this.ctx.chase();
    if (chase) {
      const sweeping = chase.spec.chaseStarted === true && !this.flags.c3;
      this.ctx.world.setColliderEnabled(chase.mesh, sweeping);
      const mesh = chase.mesh as unknown as { material: { opacity: number } };
      if (mesh.material) mesh.material.opacity = sweeping ? 1 : 0.35;
    }
  }

  /** Teks tujuan aktif — selalu jelas apa yang harus dilakukan. */
  private updateObjective(): void {
    if (this.finished) {
      this.ctx.objective(null);
      return;
    }
    if (!this.flags.c1) this.ctx.objective('① Dorong crate naik ramp ke atas');
    else if (!this.flags.c2) this.ctx.objective('② Bawa crate naik anak tangga');
    else if (!this.flags.c3) this.ctx.objective('③ LARI! Jangan sampai tersusul dinding');
    else this.ctx.objective('④ Dorong crate ke lingkaran finish');
  }

  // ---- Checkpoint ----

  private evalC1(p: RuntimePlayer): void {
    if (this.flags.c1) return;
    const zone = this.cp?.c1;
    if (!zone) return;
    for (let i = 0; i < zone.crates.length; i++) {
      const crate = this.ctx.pushables[zone.crates[i]];
      const [tx, tz, r] = zone.targets[i];
      if (!crate) return;
      const dx = crate.pos.x - tx;
      const dz = crate.pos.z - tz;
      if (Math.hypot(dx, dz) > r) return;
    }
    // Pemain harus benar-benar pindah ke bank jauh (grounded).
    if (p.grounded && p.z > zone.bankZ) {
      this.flags.c1 = true;
      this.ctx.hint('BAGIAN 1 Selesai — lanjut!');
      this.ctx.setGateOpen(0);
    }
  }

  private evalC2(p: RuntimePlayer): void {
    if (this.flags.c2) return;
    const zone = this.cp?.c2;
    if (!zone) return;
    const onPad3 =
      p.grounded &&
      GROUNDED_STATES.has(p.state) &&
      Math.abs(p.x) <= zone.xMax &&
      p.z >= zone.zFrom &&
      p.z <= zone.zTo &&
      p.y >= (zone.yMin ?? -Infinity);
    if (onPad3) {
      this.flags.c2 = true;
      this.ctx.hint('BAGIAN 2 Selesai — hati-hati Bagian 3!');
      this.ctx.setGateOpen(1);
    }
  }

  private evalC3(p: RuntimePlayer, delta: number): void {
    if (!this.flags.c2) return;
    const zone = this.cp?.c3;
    const chase = this.ctx.chase();
    if (!zone || !chase) return;

    if (this.flags.c3) {
      chase.spec.chaseStarted = false;
      chase.spec.chaseBlocked = false;
      return;
    }

    // Mulai mengejar saat pemain melewati dinding yang diam.
    if (!chase.spec.chaseStarted && p.z > zone.startZ) {
      chase.spec.chaseStarted = true;
      this.ctx.hint('LARI!');
    }

    // Anti-crush: tahan sapuan bila ada crate terjepit di depannya
    // (maju terus = memompa crate tembus collider).
    chase.spec.chaseBlocked = chase.spec.chaseStarted && this.isChaseCrushRisk(chase);

    // Gagal: (a) tenggelam/jatuh di S3, (b) muka dinding sudah < 0.35 m
    // di belakang pemain — reset SEBELUM terjepit, bukan sesudah.
    const overtake = chase.spec.chaseStarted && chase.pz + chase.spec.d / 2 > p.z - 0.35;
    const fell = chase.spec.chaseStarted && p.y < -0.1;
    if ((overtake || fell) && this.retryCooldown <= 0) {
      this.retryCooldown = 1;
      this.resetSection3(chase);
      return;
    }

    if (p.z > zone.lineZ) {
      // Satu crate full-stage: crate wajib ikut melewati garis.
      if (zone.requireCrate !== undefined) {
        const crate = this.ctx.pushables[zone.requireCrate];
        if (!crate || crate.pos.z <= zone.lineZ) return;
      }
      this.flags.c3 = true;
      chase.spec.chaseStarted = false;
      chase.spec.chaseBlocked = false;
      this.ctx.hint('BAGIAN 3 Selesai — dorong sampai finish!');
      this.ctx.setGateOpen(2);
    }
    void delta;
  }

  private resetSection3(chase: MovingPlatform): void {
    const zone = this.cp?.c3;
    if (!zone) return;
    chase.spec.chaseStarted = false;
    chase.spec.chaseBlocked = false;
    chase.pz = chase.spec.z ?? 12.3;
    chase.collider.min.z = chase.pz - chase.spec.d / 2;
    chase.collider.max.z = chase.pz + chase.spec.d / 2;
    chase.mesh.position.z = chase.pz;
    this.ctx.resetPlayer(this.ctx.player().x, zone.resetZ);
    this.ctx.hint('Tertangkap! Ulangi Bagian 3.');
  }

  /**
   * True bila ada crate tepat di depan muka dinding SEMENTARA crate itu
   * sendiri terhimpit solid aktif di depannya (gap < 0.3 m di kedua sisi,
   * lebih kecil dari badan crate): sapuan wajib ditahan, kalau tidak
   * resolveBox memompa crate menembus salah satu collider.
   */
  private isChaseCrushRisk(chase: MovingPlatform): boolean {
    const front = chase.pz + chase.spec.d / 2;
    for (const crate of this.ctx.pushables) {
      const hz = crate.halfZ ?? 0.7;
      const hx = crate.halfX ?? 0.7;
      const height = crate.height ?? 1.0;
      const back = crate.pos.z - hz;
      const gapWall = back - front;
      if (gapWall > 0.3 || gapWall < -0.6) continue;
      const cf = crate.pos.z + hz;
      for (const col of this.ctx.world.colliders) {
        if (col.disabled || col.object === chase.mesh) continue;
        if (col.kind !== 'box' && col.kind !== 'platform') continue;
        if (col.max.x < crate.pos.x - hx - 0.05 || col.min.x > crate.pos.x + hx + 0.05) continue;
        const gapAhead = col.min.z - cf;
        if (gapAhead > 0.3 || gapAhead < -0.6) continue;
        // Solid harus benar-benar setinggi badan crate (lantai/deck
        // di bawah kaki tidak dihitung sebagai himpitan).
        if (col.max.y < crate.pos.y + 0.1) continue;
        if (col.min.y > crate.pos.y + height) continue;
        return true;
      }
    }
    return false;
  }

  // ---- Watchdog crate (anti-softlock) ----

  /** Crate jembatan yang terparkir TEPAT di targetnya: biarkan (bukan macet). */
  private isParkedBridge(i: number): boolean {
    const zone = this.cp?.c1;
    if (!zone) return false;
    const ti = zone.crates.indexOf(i);
    if (ti < 0 || ti >= zone.targets.length) return false;
    const crate = this.ctx.pushables[i];
    if (!crate) return false;
    const [tx, tz, r] = zone.targets[ti];
    return Math.hypot(crate.pos.x - tx, crate.pos.z - tz) <= r;
  }

  private watchdogCrates(delta: number): void {
    for (let i = 0; i < this.ctx.pushables.length; i++) {
      if (this.isParkedBridge(i)) continue;
      const crate = this.ctx.pushables[i];
      const inWater = this.ctx.world.waterAt(crate.pos.x, crate.pos.z) !== null;
      if (!inWater) {
        // Daratan kering = titik valid terakhir (tujuan respawn).
        this.lastValid[i] = { x: crate.pos.x, z: crate.pos.z };
      }
      const slow = this.ctx.crateSpeed(i) < 0.1;
      const t = this.waterGrace.get(i) ?? 0;
      const next = inWater && slow ? t + delta : 0;
      this.waterGrace.set(i, next);
      if (next > 2) {
        this.waterGrace.set(i, 0);
        const spot = this.lastValid[i] ?? { x: crate.pos.x, z: crate.pos.z };
        this.ctx.respawnPushable(i, spot.x, spot.z);
        this.ctx.hint('Crate kembali ke posisi terakhir yang aman.');
      }
    }
  }

  // ---- Finish settle ----

  private evalFinishSettle(delta: number): void {
    const settle = this.def.finishSettle;
    if (!settle) return;
    const idx = this.def.finishCrate ?? 0;
    const crate = this.ctx.pushables[idx];
    if (!crate) return;
    const inZone = this.ctx.inFinishZone(crate.pos.x, crate.pos.z);
    const calm = this.ctx.crateSpeed(idx) < settle.speedMax;
    if (inZone && calm) {
      this.sledTimer += delta;
      if (this.sledTimer >= settle.time) this.finished = true;
    } else {
      this.sledTimer = 0;
    }
  }
}